import { createFreePreviewMember } from 'actions/LuxPlusActions'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Colour from 'components/utils/Colour'
import FormatDateTimeRange from 'components/utils/Formatters/FormatDateTimeRange'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getImageUrl } from 'lib/image/imageUtils'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusRenewalWarningSheet from 'luxPlus/components/Banners/LuxPlusRenewalWarningSheet'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getCanRedeemLuxPlusBenefits, getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { LUX_PLUS_MONTH_HIGHLIGHT_BLUE, LUX_PLUS_MONTH_TAKEOVER_END_DATE, LUX_PLUS_MONTH_TAKEOVER_START_DATE } from '../constants/luxPlusMonthTakeoverConstants'
import LuxPlusMonthCalendarLogo from './LuxPlusMonthCalendarLogo'
import { LUX_PLUS_MONTH_LANDING_PAGE_CAROUSELS_SECTION_ID } from './LuxPlusMonthLandingPageCarousels'

const Hero = styled(Sheet)`
  overflow: hidden;
  background:
    url(${getImageUrl('lux-plus-month-plus-texture-mobile')}) no-repeat bottom / 100%,
    linear-gradient(40deg, rgb(12, 21, 110) 0%, 9.80149%, rgb(15, 20, 118) 19.603%, 24.1522%, rgb(18, 21, 138) 28.7014%, 29.694%, rgb(23, 24, 145) 30.6865%, 34.4086%, rgb(32, 33, 153) 38.1307%, 41.5633%, rgb(27, 30, 155) 44.9959%, 51.4888%, rgb(25, 35, 167) 57.9818%, 60.091%, rgb(32, 44, 174) 62.2002%, 63.6476%, rgb(42, 54, 180) 65.0951%, 68.4036%, rgb(54, 69, 190) 71.7122%, 78.7014%, rgb(64, 84, 203) 85.6907%, 92.8453%, rgb(72, 102, 222) 100%);

  ${mediaQueryUp.tablet} {
    background:
      url(${getImageUrl('lux-plus-month-plus-texture-desktop')}) no-repeat bottom center / ${rem(1440)},
      radial-gradient(circle at top left, rgb(12, 21, 110) 0%, 9.80149%, rgb(15, 20, 118) 19.603%, 24.1522%, rgb(18, 21, 138) 28.7014%, 29.694%, rgb(23, 24, 145) 30.6865%, 34.4086%, rgb(32, 33, 153) 38.1307%, 41.5633%, rgb(27, 30, 155) 44.9959%, 51.4888%, rgb(25, 35, 167) 57.9818%, 60.091%, rgb(32, 44, 174) 62.2002%, 63.6476%, rgb(42, 54, 180) 65.0951%, 68.4036%, rgb(54, 69, 190) 71.7122%, 78.7014%, rgb(64, 84, 203) 85.6907%, 92.8453%, rgb(72, 102, 222) 100%);
  }
`

const ContentContainer = styled(LayoutContainer)`
  padding-inline: 0;
  display: grid;
  grid-template:
    "tiles-container" ${rem(100)}
    "content" auto / 1fr;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(524)};
    grid-template: "content tiles-container" 1fr / minmax(auto, ${rem(600)}) minmax(0, 1fr);
  }
`

const TilesContainer = styled.div`
  grid-area: tiles-container;
  position: relative;
  overflow: visible;
  align-self: stretch;
  pointer-events: none;
`

const Tiles = styled.div`
  background-image: url(${getImageUrl('mgi29kh8kp5rnk8qsj3')});
  background-size: contain;
  background-position: right center;
  background-repeat: no-repeat;
  position: absolute;
  right: -14%;
  top: -45%;
  height: ${rem(224)};
  width: ${rem(344)};

  ${mediaQueryUp.tablet} {
    background-image: url(${getImageUrl('lux-plus-month-landing-page-tiles-desktop-2')});
    background-position: left center;
    background-size: contain;
    height: ${rem(849)};
    width: ${rem(765)};
    left: 0;
    top: 50%;
    right: unset;
    transform: translateY(-50%);
  }
`

const Content = styled(LayoutContainer)`
  grid-area: content;
  align-self: end;
  padding-block-end: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(20)};
  }
`

const SubHeadingDate = styled(Heading)`
  color: ${LUX_PLUS_MONTH_HIGHLIGHT_BLUE};
`

function LuxPlusMonthLandingPageHero() {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const openLoginModal = useCallback(() => showModal<AccountAccessModalResult>(<AccountAccessModal />), [showModal])

  const dispatch = useAppDispatch()
  const startFreePreview = useCallback(async() => {
    if (!isLoggedIn) {
      const loginResult = await openLoginModal()
      if (loginResult.loggedIn) {
        dispatch(createFreePreviewMember())
      }
    } else {
      dispatch(createFreePreviewMember())
    }
  }, [dispatch, isLoggedIn, openLoginModal])

  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  const intl = useIntl()
  const startFreePreviewLabel = intl.formatMessage({
    id: 'DVHnZnSjgB',
    defaultMessage: 'Start your FREE preview today',
    description: 'Button text to start free LuxPlus+ preview on LuxPlus+ Month landing page hero',
  })
  const exploreOffersLabel = intl.formatMessage({
    id: 'VIprZriyxA',
    defaultMessage: 'Explore our top offers',
    description: 'Button text to scroll to top offers on LuxPlus+ Month landing page hero',
  })

  return <Hero paletteType="inverse">
    <ContentContainer>
      <Content>
        <Sheet padding="0 0 32 0" tabletPadding="0 0 64 0">
          <Group direction="vertical" gap={20}>
            <Group direction="vertical" gap={16} tabletGap={32}>
              <Group direction="vertical" gap={8}>
                <div>
                  <LuxPlusMonthCalendarLogo size="M" />
                </div>
                <Heading variant="heading1">
                  <FormattedMessage
                    id="dp5PkLBaTE"
                    defaultMessage="LuxPlus<colorPlus>+</colorPlus> Month"
                    description="Brand heading for LuxPlus+ Month campaign on landing page hero"
                    values={{
                      colorPlus: (chunks) => <Colour value="highlight-lux-plus">{chunks}</Colour>,
                    }}
                  />
                </Heading>
                <SubHeadingDate variant="heading5">
                  <FormatDateTimeRange startValue={LUX_PLUS_MONTH_TAKEOVER_START_DATE} endValue={LUX_PLUS_MONTH_TAKEOVER_END_DATE} day="numeric" month="long" />
                </SubHeadingDate>
              </Group>
              <Group direction="vertical" gap={12}>
                <Heading variant="heading3">
                  <FormattedMessage
                    id="Adp+rZDWUo"
                    defaultMessage="Four weeks of <italic>elevated perks</italic> for our VIP travel club."
                    description="Heading describing LuxPlus+ Month elevated perks on landing page hero"
                    values={{
                      italic: (chunks) => <i>{chunks}</i>,
                    }}
                  />
                </Heading>
                <Heading variant="heading5">
                  {intl.formatMessage({
                    id: 'BTGDhoFF+q',
                    defaultMessage: 'More savings, more hidden offers & more exclusive benefits',
                    description: 'Subheading listing LuxPlus+ Month benefits on landing page hero',
                  })}
                </Heading>
              </Group>
            </Group>
            <Group direction="vertical" tabletHorizontalAlign="start" gap={24}>
              {!canViewLuxPlusBenefits && <TextButton kind="primary" shape="round" size="large" onClick={startFreePreview}>
                {startFreePreviewLabel}
              </TextButton>}
              {canViewLuxPlusBenefits && <TextButton kind="primary" shape="round" size="large" href={`#${LUX_PLUS_MONTH_LANDING_PAGE_CAROUSELS_SECTION_ID}`}>
                {exploreOffersLabel}
              </TextButton>}
              {!isLoggedIn && <BodyText variant="medium" align="center" tabletAlign="start">
                <FormattedMessage
                  id="3nTSCeH3ae"
                  defaultMessage="Already a {programName} member? <link>Sign in</link>"
                  description="Sign in prompt for existing LuxPlus+ members on LuxPlus+ Month landing page hero"
                  values={{
                    programName: LUXURY_PLUS.PROGRAM_NAME,
                    link: (chunks) => <TextLink onClick={openLoginModal}>{chunks}</TextLink>,
                  }}
                />
              </BodyText>}
              {(isLoggedIn && isLuxPlusMember) && <BodyText variant="medium" align="center" tabletAlign="start">
                <TextLink to={`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`}>{intl.formatMessage({
                  id: 'SRZFiL7VK8',
                  defaultMessage: 'Manage membership',
                  description: 'Link text to manage LuxPlus+ membership on LuxPlus+ Month landing page hero',
                })}
                </TextLink>
              </BodyText>}
            </Group>
            <LuxPlusRenewalWarningSheet />
          </Group>
        </Sheet>
        {!isLuxPlusMember && <Caption variant="medium">
          <FormattedMessage
            id="krTPI47551"
            defaultMessage="To book an offer with {programName} benefits during your Free Preview you will be prompted to become a member. <link>T&Cs apply</link>"
            description="Terms and conditions notice for LuxPlus+ Free Preview on LuxPlus+ Month landing page hero"
            values={{
              programName: LUXURY_PLUS.PROGRAM_NAME,
              link: (chunks) => <TextLink to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} target="_blank">{chunks}</TextLink>,
            }}
          />
        </Caption>}
      </Content>
      <TilesContainer role="presentation">
        <Tiles role="presentation" />
      </TilesContainer>
    </ContentContainer>
  </Hero>
}

export default LuxPlusMonthLandingPageHero
